<template>
  <div class="p-6">
    <div class="grid grid-cols-2 gap-x-6 gap-y-6">
      <div v-for="(field, index) in fields" :key="index" class="space-y-2">
        <label :for="field.id" class="block text-sm font-medium text-gray-700">
          {{ field.label }}
        </label>
        <div class="flex items-center space-x-2">
          <div class="flex-grow">
            <template v-if="field.id === 'drug_allergy'">
              <vue-multiselect
                v-model="field.value"
                :options="drugAllergyOptions"
                :multiple="true"
                :taggable="true"
                track-by="id"
                label="display_name"
                placeholder="Search drug catalog..."
                @search-change="onDrugAllergySearch"
                @tag="addDrugAllergyTag"
                :loading="isSearchingDrugs"
              />
              <p
                v-if="existingFreeTextAllergies.length"
                class="mt-1 text-md flex space-x-2"
              >
                <span>Also on record but unlinked:</span>
                <span class="text-amber-600"> {{ existingFreeTextAllergies.join(', ') }}</span>
              </p>
            </template>

            <vue-multiselect
              v-else
              v-model="field.value"
              :options="field.options"
              :multiple="true"
              :taggable="true"
              @tag="addTag(field.id, $event)"
              placeholder="Type..."
              class="w-full"
            />
          </div>
          <button
            @click="setNone(field.id)"
            class="bg-blue-900 text-white px-3 py-2 rounded hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 whitespace-nowrap"
          >
            None
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueMultiselect from "vue-multiselect";
import apiClient from '@/services/api.js'

export default {
  name: "ConsultationHistoryAllergyTab",
  components: {
    VueMultiselect,
  },
  props: {
    patientId: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      fields: [
        {
          id: "food_allergy",
          label: "Food Allergy",
          value: [],
          options: ["Peanuts", "Milk"],
        },
        {
          id: "drug_allergy",
          label: "Drug Allergy",
          value: [],
          options: ["Penicillin", "Aspirin"],
        },
        {
          id: "medical_history",
          label: "Past Medical History",
          value: [],
          options: ["Diabetic"],
        },
        {
          id: "current_medicine",
          label: "Current Medication",
          value: [],
          options: [],
        },
        { id: "drug_history", label: "Drug History", value: [], options: [] },
        {
          id: "family_history",
          label: "Family History",
          value: [],
          options: [],
        },
        {
          id: "triage_medicine",
          label: "Triage Medicine",
          value: [],
          options: [],
        },
        {
          id: "surgical_history",
          label: "Surgical History",
          value: [],
          options: []
        }
      ],

      drugAllergyOptions: [],
      existingFreeTextAllergies: [],
      isFetchingAllergies: false,
      isSearchingDrugs: false,
      searchTimeout: null
    };
  },

  async mounted() {
    if (!this.patientId) return;

    this.isFetchingAllergies = true;
    try {
      const response = await apiClient.getPatient(this.patientId)
      if (response.success && response.data.drug_allergies?.length) {
        
        const drugAllergiesField = this.fields.find((f) => f.id === 'drug_allergy')
        const structured = response.data.drug_allergies.filter((a) => a.inventory_item_id)
        const freeText = response.data.drug_allergies.filter((a) => !a.inventory_item_id && a.name && a.name !== 'None')

        drugAllergiesField.value = structured.map((a) => ({
          id: a.inventory_item_id,
          drug_category_id: a.drug_category_id,
          display_name: a.inventory_item
            ? `${a.inventory_item.trade_name}${a.inventory_item.generic_name ? ` (${a.inventory_item.generic_name})` : ''}`
            : a.name 
        }));

        this.existingFreeTextAllergies = freeText.map((a) => a.name)
      }
    } catch {
      //
    } finally {
      this.isFetchingAllergies = false;
    }
  },

  methods: {
    onDrugAllergySearch(query) {
      if (query.length < 3) return;

      clearTimeout(this.searchTimeout);
      this.isSearchingDrugs = true;
      this.searchTimeout = setTimeout(async () => {
        try {
          const response = await apiClient.searchConsultationInventoryItems({name: query})
          if (response.success) {
            this.drugAllergyOptions = response.data.map((item) => ({
              id: item.id,
              drug_category_id: item.drug_category_id,
              display_name: `${item.trade_name}${item.generic_name ? `(${item.generic_name})` : ""}`
            }))
          }
        } catch{
          this.$toast.error('Unable to search drug catalog.')
        } finally {
          this.isSearchingDrugs = false
        }
      }, 300)
    },

    addTag(fieldId, newTag) {
      const field = this.fields.find((f) => f.id === fieldId);
      field.options.push(newTag);
      field.value.push(newTag);
    },

    addDrugAllergyTag(newTag) {
      const field = this.fields.find((f) => f.id === 'drug_allergy')
      
      const tagObj = {
        id: `free-text-${Date.now()}`,
        drug_category_id: null,
        display_name: newTag,
        is_free_text: true
      }

      this.drugAllergyOptions.push(tagObj)
      field.value.push(tagObj)
    },

    setNone(fieldId) {
      const field = this.fields.find((f) => f.id === fieldId);

      if (fieldId === 'drug_allergy') {
        field.value = [{ id: null, drug_category_id: null, display_name: "None" }]
      } else {
        field.value = ["None"];
      }
    },
    submitHistory() {
      const formData = this.fields.reduce((acc, field) => {
        if (field.id == 'drug_allergy') {
          acc[field.id] = field.value.map((item) => 
            typeof item === 'string'
              ? { name: item }
              : { inventory_item_id: item.is_free_text ? null : item.id, drug_category_id: item.drug_category_id, name: item.display_name }
          );
        } else {
          acc[field.id] = field.value;
        }
        return acc;
      }, {});
      this.$emit("history-submitted", formData);
    },
  },
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style scoped>
.multiselect__tags {
  border-radius: 0.375rem;
  border: 1px solid #d1d5db;
  padding: 0.5rem;
  min-height: 40px;
}

.multiselect__tag {
  background-color: #e5e7eb;
  color: #374151;
}

.multiselect__option--highlight {
  background-color: #2563eb;
  color: white;
}

.multiselect__input {
  min-height: 20px;
}
</style>
